/* eslint-disable no-unused-vars */
<template>
  <div class="super-admin-container">
    <h1 class="title">📊 총괄 관리자 페이지</h1>
    <router-link to="/dashboard" class="back-button">
          ◀️ 대시보드로 돌아가기
    </router-link>

    <!-- 1. 전체 매출 요약 섹션 -->
    <section class="section summary-section">
      <h2>💰 전체 플랫폼 매출 현황</h2>
      
      <!-- 월 선택 -->
      <div class="month-selector">
        <button @click="prevMonth">◀</button>
        <span>{{ year }}년 {{ month }}월</span>
        <button @click="nextMonth">▶</button>
      </div>

      <!-- 전체 매출 요약 카드 -->
      <div class="total-summary-cards">
        <div class="summary-card">
          <span class="card-label">총 매출</span>
          <span class="card-value">{{ formatPrice(totalPlatformSales) }}원</span>
        </div>
        <div class="summary-card">
          <span class="card-label">총 예약 건수</span>
          <span class="card-value">{{ totalPlatformCount }}건</span>
        </div>
        <div class="summary-card">
          <!--2027년부터 3프로 적용<span class="card-label">총 수수료 (10%)</span>-->
          <span class="card-label">수수료 없음</span>
          
          <span class="card-value fee">{{ formatPrice(totalPlatformFee) }}원</span>
        </div>
        <div class="summary-card">
          <span class="card-label">등록 매장 수</span>
          <span class="card-value">{{ uniqueShops.length }}개</span>
        </div>
      </div>

      <!-- 전체 플랫폼 월별 매출 차트 -->
      <div class="chart-section">
        <h3>📈 월별 플랫폼 매출 추이</h3>
        <div class="chart-container" v-if="platformChartReady">
          <canvas ref="platformChart"></canvas>
        </div>
        <div class="chart-container loading" v-else>
          <p>차트 로딩 중...</p>
        </div>
      </div>
    </section>

    <!-- 2. 매장 비교 차트 섹션 -->
    <section class="section compare-section">
      <h2>📊 매장별 매출 비교</h2>
      
      <!-- 매장 선택 체크박스 -->
      <div class="shop-selector">
        <label v-for="shop in uniqueShops" :key="shop.id" class="shop-checkbox">
          <input 
            type="checkbox" 
            :value="shop.id" 
            v-model="selectedShopsForCompare"
            @change="updateCompareChart"
          />
          {{ shop.name }}
        </label>
      </div>

      <!-- 비교 차트 -->
      <div class="chart-section" v-if="selectedShopsForCompare.length > 0">
        <div class="chart-container" v-if="compareChartReady">
          <canvas ref="compareChart"></canvas>
        </div>
        <div class="chart-container loading" v-else>
          <p>차트 로딩 중...</p>
        </div>
      </div>
      <div v-else class="empty-selection">
        <p>비교할 매장을 선택해주세요</p>
      </div>
    </section>

    <!-- 3. 매장별 상세 섹션 -->
    <section class="section">
      <h2>🏪 매장별 상세 현황</h2>
      <div class="shop-cards">
        <div 
          class="shop-card"
          v-for="shop in uniqueShops" 
          :key="shop.id"
          @click="openShopDetail(shop)"
        >
          <div class="shop-card-header">
            <h3>{{ shop.name }}</h3>
            <span class="region-badge">{{ getRegion(shop.address) }}</span>
          </div>
          <p class="shop-address">{{ shop.address }}</p>
          <div class="shop-stats">
            <div class="stat">
              <span class="stat-label">이번 달 매출</span>
              <span class="stat-value">{{ formatPrice(getShopMonthSales(shop.id)) }}원</span>
            </div>
            <div class="stat">
              <span class="stat-label">예약 건수</span>
              <span class="stat-value">{{ getShopMonthCount(shop.id) }}건</span>
            </div>
          </div>
          <button class="detail-btn">상세보기 →</button>
        </div>
      </div>
    </section>

    <!-- 매장 상세 모달 -->
    <div class="modal-overlay" v-if="selectedShop" @click.self="closeShopDetail">
      <div class="modal-content">
        <div class="modal-header">
          <h2>{{ selectedShop.name }}</h2>
          <button class="close-btn" @click="closeShopDetail">✕</button>
        </div>
        
        <div class="modal-body">
          <!-- 월 선택 -->
          <div class="month-selector">
            <button @click="prevModalMonth">◀</button>
            <span>{{ modalYear }}년 {{ modalMonth }}월</span>
            <button @click="nextModalMonth">▶</button>
          </div>

          <!-- 개별 매장 차트 -->
          <div class="chart-section">
            <h3>📈 월별 매출 추이</h3>
            <div class="chart-container" v-if="shopChartReady">
              <canvas ref="shopDetailChart"></canvas>
            </div>
            <div class="chart-container loading" v-else>
              <p>차트 로딩 중...</p>
            </div>
          </div>

          <!-- 정산 요약 -->
          <div class="summary-card-modal">
            <div class="summary-item">
              <span class="label">총 매출</span>
              <span class="value">{{ formatPrice(selectedShopSummary.totalSales) }}원</span>
            </div>
            <div class="summary-item">
              <span class="label">건수</span>
              <span class="value">{{ selectedShopSummary.totalCount }}건</span>
            </div>
            <div class="summary-item">
              <!--2027년부터 3프로 적용<span class="label">수수료 (10%)</span>-->
              <span class="label">수수료 없음</span>
              <span class="value negative">-{{ formatPrice(selectedShopSummary.fee) }}원</span>
            </div>
            <div class="summary-item total">
              <span class="label">정산 예정액</span>
              <span class="value highlight">{{ formatPrice(selectedShopSummary.settlement) }}원</span>
            </div>
          </div>

          <!-- 거래 내역 -->
          <div class="transaction-section">
            <h3>📋 거래 내역</h3>
            <div class="transaction-list" v-if="selectedShopSummary.reservations?.length">
              <div 
                class="transaction-item" 
                v-for="item in selectedShopSummary.reservations" 
                :key="item._id"
              >
                <div class="tx-date">{{ formatDate(item.date) }}</div>
                <div class="tx-info">
                  <span class="tx-service">{{ item.service || item.serviceName || '서비스' }}</span>
                  <span class="tx-status" :class="item.status">{{ statusText(item.status) }}</span>
                </div>
                <div class="tx-price">{{ formatPrice(item.price) }}원</div>
              </div>
            </div>
            <div v-else class="empty">거래 내역이 없습니다.</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 기존 전체 예약 내역 (접이식) -->
    <section class="section collapsible-section">
      <h2 @click="toggleReservationList" class="collapsible-header">
        📅 전체 예약 내역 
        <span class="toggle-icon">{{ showReservationList ? '▼' : '▶' }}</span>
      </h2>
      <div v-if="showReservationList">
        <div v-for="(group, shopName) in groupedReservations" :key="shopName" class="region-group">
          <h3>💈 {{ shopName }}</h3>
          <ul>
            <li v-for="res in group" :key="res._id">
              {{ formatFullDate(res.date) }} {{ res.time }} / {{ res.userName }} - {{ formatPrice(res.price) }}원
              <span class="status-badge" :class="res.status">{{ statusText(res.status) }}</span>
            </li>
          </ul>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios';
import Chart from 'chart.js/auto';

export default {
  name: 'SuperAdminView',
  data() {
    return {
      shops: [],
      reservations: [],
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      
      // 플랫폼 전체 차트
      platformChartReady: false,
      platformChart: null,
      platformMonthlyData: [],
      
      // 매장 비교 차트
      selectedShopsForCompare: [],
      compareChartReady: false,
      compareChart: null,
      compareMonthlyData: {},
      
      // 매장 상세 모달
      selectedShop: null,
      modalYear: new Date().getFullYear(),
      modalMonth: new Date().getMonth() + 1,
      shopChartReady: false,
      shopDetailChart: null,
      selectedShopMonthlyData: [],
      selectedShopSummary: {
        totalSales: 0,
        totalCount: 0,
        fee: 0,
        settlement: 0,
        reservations: [],
      },
      
      // 각 매장별 현재 월 데이터 캐시
      shopMonthlySalesCache: {},
      
      // 접이식 예약 목록
      showReservationList: false,
    };
  },
  
  computed: {
    uniqueShops() {
      const seen = new Set();
      return this.shops.filter(shop => {
        const uniqueKey = `${shop.name}_${shop.address}`;
        if (seen.has(uniqueKey)) return false;
        seen.add(uniqueKey);
        return true;
      });
    },
    
    groupedReservations() {
      const groups = {};
      for (const res of this.reservations) {
        const name = res.barbershopName || res.shopName || '기타';
        if (!groups[name]) groups[name] = [];
        groups[name].push(res);
      }
      return groups;
    },
    
    totalPlatformSales() {
      return Object.values(this.shopMonthlySalesCache).reduce((sum, data) => sum + (data.totalSales || 0), 0);
    },
    
    totalPlatformCount() {
      return Object.values(this.shopMonthlySalesCache).reduce((sum, data) => sum + (data.totalCount || 0), 0);
    },
    //2027년부터 3프로 적용
    totalPlatformFee() {
      //return Math.round(this.totalPlatformSales * 0.10);
      return 0;
    
    },
  },
  
  async created() {
    try {
      const shopRes = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/api/barbershops/all`);
      this.shops = shopRes.data;

      const resRes = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/api/reservations/all`);
      this.reservations = resRes.data;
      
      // 데이터 로드 후 차트 초기화
      await this.fetchAllShopsMonthlySales();
      await this.fetchPlatformMonthlyData();
    } catch (e) {
      console.error('❌ 총괄 데이터 불러오기 실패:', e);
    }
  },
  
  beforeUnmount() {
    if (this.platformChart) this.platformChart.destroy();
    if (this.compareChart) this.compareChart.destroy();
    if (this.shopDetailChart) this.shopDetailChart.destroy();
  },
  
  methods: {
    // ===== 데이터 페칭 =====
    async fetchAllShopsMonthlySales() {
      const cache = {};
      for (const shop of this.uniqueShops) {
        try {
          const res = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/api/settlements/summary`, {
            params: {
              barbershopId: shop.id,
              year: this.year,
              month: this.month,
            }
          });
          const reservations = res.data.reservations || [];
          const totalSales = reservations.reduce((sum, r) => sum + (Number(r.price) || 0), 0);
          cache[shop.id] = {
            totalSales,
            totalCount: reservations.length,
            //fee: Math.round(totalSales * 0.10),
            fee: 0,
          };
        } catch (err) {
          cache[shop.id] = { totalSales: 0, totalCount: 0, fee: 0 };
        }
      }
      this.shopMonthlySalesCache = cache;
    },
    
    async fetchPlatformMonthlyData() {
      this.platformChartReady = false;
      
      try {
        const months = this.getLast6Months();
        const monthlyTotals = [];
        
        for (const { year, month } of months) {
          let totalSales = 0;
          let totalCount = 0;
          
          for (const shop of this.uniqueShops) {
            try {
              const res = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/api/settlements/summary`, {
                params: {
                  barbershopId: shop.id,
                  year,
                  month,
                }
              });
              const reservations = res.data.reservations || [];
              totalSales += reservations.reduce((sum, r) => sum + (Number(r.price) || 0), 0);
              totalCount += reservations.length;
            } catch (err) {
              // 개별 매장 에러는 무시
            }
          }
          
          monthlyTotals.push({ year, month, totalSales, totalCount });
        }
        
        this.platformMonthlyData = monthlyTotals;
        this.platformChartReady = true;
        
        this.$nextTick(() => {
          setTimeout(() => this.renderPlatformChart(), 100);
        });
      } catch (err) {
        console.error('❌ 플랫폼 월별 데이터 조회 실패:', err);
      }
    },
    
    async fetchShopMonthlyData(shopId) {
      this.shopChartReady = false;
      
      try {
        const months = this.getLast6MonthsModal();
        const promises = months.map(({ year, month }) => 
          axios.get(`${process.env.VUE_APP_API_BASE_URL}/api/settlements/summary`, {
            params: { barbershopId: shopId, year, month }
          }).then(res => {
            const reservations = res.data.reservations || [];
            return {
              year,
              month,
              totalSales: reservations.reduce((sum, r) => sum + (Number(r.price) || 0), 0),
              count: reservations.length,
            };
          }).catch(() => ({
            year,
            month,
            totalSales: 0,
            count: 0,
          }))
        );

        this.selectedShopMonthlyData = await Promise.all(promises);
        this.shopChartReady = true;
        
        this.$nextTick(() => {
          setTimeout(() => this.renderShopDetailChart(), 100);
        });
      } catch (err) {
        console.error('❌ 매장 월별 데이터 조회 실패:', err);
      }
    },
    
    async fetchShopSummary(shopId) {
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/api/settlements/summary`, {
          params: {
            barbershopId: shopId,
            year: this.modalYear,
            month: this.modalMonth,
          }
        });
        const reservations = res.data.reservations || [];
        const totalSales = reservations.reduce((sum, r) => sum + (Number(r.price) || 0), 0);
        
        this.selectedShopSummary = {
          totalSales,
          totalCount: reservations.length,
          fee: Math.round(totalSales * 0.10),
          settlement: totalSales - Math.round(totalSales * 0.10),
          reservations,
        };
      } catch (err) {
        console.error('❌ 매장 정산 조회 실패:', err);
      }
    },
    
    async updateCompareChart() {
      if (this.selectedShopsForCompare.length === 0) {
        this.compareChartReady = false;
        return;
      }
      
      this.compareChartReady = false;
      const months = this.getLast6Months();
      const compareData = {};
      
      for (const shopId of this.selectedShopsForCompare) {
        const shop = this.uniqueShops.find(s => s.id === shopId);
        if (!shop) continue;
        
        const monthlyData = [];
        for (const { year, month } of months) {
          try {
            const res = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/api/settlements/summary`, {
              params: { barbershopId: shopId, year, month }
            });
            const reservations = res.data.reservations || [];
            monthlyData.push(reservations.reduce((sum, r) => sum + (Number(r.price) || 0), 0));
          } catch (err) {
            monthlyData.push(0);
          }
        }
        compareData[shop.name] = monthlyData;
      }
      
      this.compareMonthlyData = compareData;
      this.compareChartReady = true;
      
      this.$nextTick(() => {
        setTimeout(() => this.renderCompareChart(), 100);
      });
    },
    
    // ===== 차트 렌더링 =====
    renderPlatformChart() {
      const canvas = this.$refs.platformChart;
      if (!canvas) return;
      
      if (this.platformChart) {
        this.platformChart.destroy();
      }
      
      const labels = this.platformMonthlyData.map(d => `${d.month}월`);
      const salesData = this.platformMonthlyData.map(d => d.totalSales);
      const countData = this.platformMonthlyData.map(d => d.totalCount);
      
      this.platformChart = new Chart(canvas.getContext('2d'), {
        type: 'line',
        data: {
          labels,
          datasets: [
            {
              label: '총 매출 (원)',
              data: salesData,
              borderColor: '#007bff',
              backgroundColor: 'rgba(0, 123, 255, 0.1)',
              fill: true,
              tension: 0.3,
              yAxisID: 'y',
            },
            {
              label: '총 예약 건수',
              data: countData,
              borderColor: '#28a745',
              backgroundColor: 'rgba(40, 167, 69, 0.1)',
              fill: false,
              tension: 0.3,
              yAxisID: 'y1',
            }
          ]
        },
        options: this.getChartOptions('플랫폼 전체'),
      });
    },
    
    renderCompareChart() {
      const canvas = this.$refs.compareChart;
      if (!canvas) return;
      
      if (this.compareChart) {
        this.compareChart.destroy();
      }
      
      const labels = this.getLast6Months().map(d => `${d.month}월`);
      const colors = ['#007bff', '#28a745', '#dc3545', '#ffc107', '#17a2b8', '#6f42c1'];
      
      const datasets = Object.entries(this.compareMonthlyData).map(([shopName, data], index) => ({
        label: shopName,
        data,
        borderColor: colors[index % colors.length],
        backgroundColor: 'transparent',
        tension: 0.3,
      }));
      
      this.compareChart = new Chart(canvas.getContext('2d'), {
        type: 'line',
        data: { labels, datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'top' },
            tooltip: {
              callbacks: {
                label: (context) => `${context.dataset.label}: ${context.raw.toLocaleString()}원`
              }
            }
          },
          scales: {
            y: {
              title: { display: true, text: '매출 (원)' },
              ticks: { callback: (v) => v.toLocaleString() + '원' }
            }
          }
        },
      });
    },
    
    renderShopDetailChart() {
      const canvas = this.$refs.shopDetailChart;
      if (!canvas) return;
      
      if (this.shopDetailChart) {
        this.shopDetailChart.destroy();
      }
      
      const labels = this.selectedShopMonthlyData.map(d => `${d.month}월`);
      const salesData = this.selectedShopMonthlyData.map(d => d.totalSales);
      const countData = this.selectedShopMonthlyData.map(d => d.count);
      
      this.shopDetailChart = new Chart(canvas.getContext('2d'), {
        type: 'line',
        data: {
          labels,
          datasets: [
            {
              label: '매출 (원)',
              data: salesData,
              borderColor: '#007bff',
              backgroundColor: 'rgba(0, 123, 255, 0.1)',
              fill: true,
              tension: 0.3,
              yAxisID: 'y',
            },
            {
              label: '예약 건수',
              data: countData,
              borderColor: '#28a745',
              fill: false,
              tension: 0.3,
              yAxisID: 'y1',
            }
          ]
        },
        options: this.getChartOptions(this.selectedShop?.name),
      });
    },
    
    getChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { position: 'top' },
          tooltip: {
            callbacks: {
              label: (context) => {
                if (context.dataset.label.includes('매출')) {
                  return `매출: ${context.raw.toLocaleString()}원`;
                }
                return `예약: ${context.raw}건`;
              }
            }
          }
        },
        scales: {
          y: {
            type: 'linear',
            display: true,
            position: 'left',
            title: { display: true, text: '매출 (원)' },
            ticks: { callback: (v) => v.toLocaleString() + '원' }
          },
          y1: {
            type: 'linear',
            display: true,
            position: 'right',
            title: { display: true, text: '예약 건수' },
            grid: { drawOnChartArea: false },
            ticks: { callback: (v) => v + '건' }
          }
        }
      };
    },
    
    // ===== 모달 관련 =====
    async openShopDetail(shop) {
      this.selectedShop = shop;
      this.modalYear = this.year;
      this.modalMonth = this.month;
      await this.fetchShopSummary(shop.id);
      await this.fetchShopMonthlyData(shop.id);
    },
    
    closeShopDetail() {
      this.selectedShop = null;
      if (this.shopDetailChart) {
        this.shopDetailChart.destroy();
        this.shopDetailChart = null;
      }
    },
    
    // ===== 월 네비게이션 =====
    prevMonth() {
      if (this.month === 1) {
        this.month = 12;
        this.year--;
      } else {
        this.month--;
      }
      this.fetchAllShopsMonthlySales();
      this.fetchPlatformMonthlyData();
      if (this.selectedShopsForCompare.length > 0) {
        this.updateCompareChart();
      }
    },
    
    nextMonth() {
      if (this.month === 12) {
        this.month = 1;
        this.year++;
      } else {
        this.month++;
      }
      this.fetchAllShopsMonthlySales();
      this.fetchPlatformMonthlyData();
      if (this.selectedShopsForCompare.length > 0) {
        this.updateCompareChart();
      }
    },
    
    async prevModalMonth() {
      if (this.modalMonth === 1) {
        this.modalMonth = 12;
        this.modalYear--;
      } else {
        this.modalMonth--;
      }
      await this.fetchShopSummary(this.selectedShop.id);
      await this.fetchShopMonthlyData(this.selectedShop.id);
    },
    
    async nextModalMonth() {
      if (this.modalMonth === 12) {
        this.modalMonth = 1;
        this.modalYear++;
      } else {
        this.modalMonth++;
      }
      await this.fetchShopSummary(this.selectedShop.id);
      await this.fetchShopMonthlyData(this.selectedShop.id);
    },
    
    // ===== 유틸리티 =====
    getLast6Months() {
      const months = [];
      let year = this.year;
      let month = this.month;
      for (let i = 0; i < 6; i++) {
        months.unshift({ year, month });
        month--;
        if (month === 0) { month = 12; year--; }
      }
      return months;
    },
    
    getLast6MonthsModal() {
      const months = [];
      let year = this.modalYear;
      let month = this.modalMonth;
      for (let i = 0; i < 6; i++) {
        months.unshift({ year, month });
        month--;
        if (month === 0) { month = 12; year--; }
      }
      return months;
    },
    
    getRegion(address) {
      return address?.split(' ')[0] || '기타';
    },
    
    getShopMonthSales(shopId) {
      return this.shopMonthlySalesCache[shopId]?.totalSales || 0;
    },
    
    getShopMonthCount(shopId) {
      return this.shopMonthlySalesCache[shopId]?.totalCount || 0;
    },
    
    formatPrice(value) {
      return (Number(value) || 0).toLocaleString();
    },
    
    formatDate(dateStr) {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      return `${date.getMonth() + 1}/${date.getDate()}`;
    },
    
    formatFullDate(dateStr) {
      const date = new Date(dateStr);
      const days = ['일', '월', '화', '수', '목', '금', '토'];
      const dayName = days[date.getUTCDay()];
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      return `${yyyy}.${mm}.${dd} (${dayName})`;
    },
    
    statusText(status) {
      const map = {
        completed: '완료',
        confirmed: '확정',
        pending: '대기',
        cancelled: '취소',
        noshow: '노쇼',
      };
      return map[status] || '대기';
    },
    
    toggleReservationList() {
      this.showReservationList = !this.showReservationList;
    },
  }
};
</script>

<style scoped>
.super-admin-container {
  padding: 24px;
  max-width: 1200px;
  margin: auto;
}

.title {
  font-size: 28px;
  font-weight: bold;
  margin-bottom: 32px;
  color: #333;
}


.back-button {
  display: inline-block;
  padding: 12px 20px;
  background-color: #6c757d;
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-weight: bold;
}

.back-button:hover {
  background-color: #5a6268;
}

.section {
  margin-bottom: 40px;
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.section h2 {
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 20px;
  color: #333;
}

/* 월 선택 */
.month-selector {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin-bottom: 24px;
}

.month-selector button {
  padding: 8px 16px;
  border: 1px solid #ddd;
  background: #fff;
  cursor: pointer;
  border-radius: 6px;
  font-size: 16px;
  transition: all 0.2s;
}

.month-selector button:hover {
  background: #f0f0f0;
}

.month-selector span {
  font-size: 20px;
  font-weight: bold;
}

/* 요약 카드 */
.total-summary-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.summary-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px;
  border-radius: 12px;
  text-align: center;
}

.summary-card .card-label {
  display: block;
  font-size: 14px;
  opacity: 0.9;
  margin-bottom: 8px;
}

.summary-card .card-value {
  display: block;
  font-size: 24px;
  font-weight: bold;
}

.summary-card .card-value.fee {
  color: #ffd700;
}

/* 차트 */
.chart-section {
  margin-top: 20px;
}

.chart-section h3 {
  font-size: 16px;
  margin-bottom: 12px;
  color: #555;
}

.chart-container {
  background: #fff;
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 16px;
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-container.loading {
  color: #888;
}

/* 매장 선택 체크박스 */
.shop-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
}

.shop-checkbox {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #f8f9fa;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s;
}

.shop-checkbox:hover {
  background: #e9ecef;
}

.shop-checkbox input {
  cursor: pointer;
}

.empty-selection {
  text-align: center;
  padding: 40px;
  color: #888;
  background: #f8f9fa;
  border-radius: 12px;
}

/* 매장 카드 */
.shop-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

.shop-card {
  background: #fff;
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s;
}

.shop-card:hover {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.shop-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.shop-card-header h3 {
  font-size: 18px;
  margin: 0;
}

.region-badge {
  background: #e9ecef;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  color: #666;
}

.shop-address {
  color: #888;
  font-size: 14px;
  margin-bottom: 16px;
}

.shop-stats {
  display: flex;
  gap: 20px;
  margin-bottom: 16px;
}

.stat {
  flex: 1;
}

.stat-label {
  display: block;
  font-size: 12px;
  color: #888;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 16px;
  font-weight: bold;
  color: #007bff;
}

.detail-btn {
  width: 100%;
  padding: 10px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: bold;
  transition: background 0.2s;
}

.detail-btn:hover {
  background: #0056b3;
}

/* 모달 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: #fff;
  border-radius: 16px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #eee;
  position: sticky;
  top: 0;
  background: #fff;
  border-radius: 16px 16px 0 0;
}

.modal-header h2 {
  margin: 0;
  font-size: 22px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #888;
}

.close-btn:hover {
  color: #333;
}

.modal-body {
  padding: 24px;
}

.summary-card-modal {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  margin: 20px 0;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #eee;
}

.summary-item:last-child {
  border-bottom: none;
}

.summary-item .label {
  color: #666;
}

.summary-item .value {
  font-weight: bold;
}

.summary-item .value.negative {
  color: #dc3545;
}

.summary-item .value.highlight {
  color: #007bff;
  font-size: 20px;
}

.summary-item.total {
  margin-top: 8px;
  padding-top: 16px;
  border-top: 2px solid #ddd;
}

/* 거래 내역 */
.transaction-section h3 {
  font-size: 16px;
  margin-bottom: 12px;
}

.transaction-list {
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
}

.transaction-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #eee;
}

.transaction-item:last-child {
  border-bottom: none;
}

.tx-date {
  color: #888;
  font-size: 14px;
  width: 60px;
}

.tx-info {
  flex: 1;
  margin-left: 12px;
}

.tx-service {
  font-weight: 500;
}

.tx-status {
  font-size: 12px;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 4px;
}

.tx-status.completed { background: #d4edda; color: #155724; }
.tx-status.confirmed { background: #cce5ff; color: #004085; }
.tx-status.pending { background: #fff3cd; color: #856404; }
.tx-status.cancelled { background: #f8d7da; color: #721c24; }

.tx-price {
  font-weight: bold;
}

.empty {
  text-align: center;
  padding: 40px;
  color: #888;
}

/* 접이식 섹션 */
.collapsible-section h2 {
  cursor: pointer;
  user-select: none;
}

.collapsible-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.toggle-icon {
  font-size: 14px;
  color: #888;
}

.region-group {
  margin-bottom: 24px;
}

.region-group h3 {
  font-size: 16px;
  margin-bottom: 12px;
  color: #555;
}

ul {
  list-style-type: none;
  padding: 0;
}

li {
  margin-bottom: 8px;
  padding: 12px 16px;
  background: #f9f9f9;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.status-badge {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
}

.status-badge.completed { background: #d4edda; color: #155724; }
.status-badge.confirmed { background: #cce5ff; color: #004085; }
.status-badge.pending { background: #fff3cd; color: #856404; }
.status-badge.cancelled { background: #f8d7da; color: #721c24; }

/* 반응형 */
@media (max-width: 768px) {
  .total-summary-cards {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .shop-cards {
    grid-template-columns: 1fr;
  }
  
  .shop-stats {
    flex-direction: column;
    gap: 8px;
  }
}
</style>